import { useCallback, useEffect, useRef, useState } from "react";
import {
  App as AntdApp,
  Avatar,
  Button,
  Empty,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import {
  PaperClipOutlined,
  SendOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useGetIdentity } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import type { UserInfo } from "../../types/auth";
import type { TicketDetail, TicketMessage } from "../../types/ticket";
import { ticketService } from "../../services/api/ticketService";
import { notifyTicketsUnreadChanged } from "../../hooks";
import { RichTextEditor } from "../common/RichTextEditor";
import { AttachmentList } from "./AttachmentList";

const { Text } = Typography;

/**
 * TicketThread — Vista de mensajes + reply de un ticket.
 *
 * Extraído del original `pages/tickets/show.tsx` para poder reutilizar
 * la misma UX de conversación desde otros sitios (pestaña "Comentarios"
 * de PlanningGroupShow, ...). Deja fuera la sidebar de status /
 * priority / resolution — esa sigue viva solo en TicketShow porque
 * incidencias necesitan ese contexto.
 *
 * Fetch propio del detalle del ticket al mount. `onChanged` se dispara
 * después de cada acción exitosa (reply, refetch) por si el padre
 * necesita refrescar contadores.
 */
interface TicketThreadProps {
  ticketId: number;
  /** Called whenever the thread state changes (new reply, refetch). */
  onChanged?: () => void;
  /** Optional overrides for the empty-state text. */
  emptyDescription?: string;
}

const MessageBubble = ({
  message,
  isMine,
}: {
  message: TicketMessage;
  isMine: boolean;
}) => {
  const { t } = useTranslation();
  const senderRoleColor = message.sender_role === 2 ? "geekblue" : "blue";
  const bg = isMine ? "#fff7e6" : "#fafafa";
  const borderColor = isMine ? "#ffd591" : "#f0f0f0";

  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        flexDirection: isMine ? "row-reverse" : "row",
      }}
    >
      <Avatar
        size={40}
        src={message.sender_avatar_url ?? undefined}
        icon={!message.sender_avatar_url && <UserOutlined />}
      />
      <div
        style={{
          flex: 1,
          maxWidth: "75%",
          backgroundColor: bg,
          border: `1px solid ${borderColor}`,
          borderRadius: 10,
          padding: 14,
          position: "relative",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 6,
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <Space size={6}>
            <strong>
              {isMine ? t("tickets.you") : message.sender_name}
            </strong>
            <Tag color={senderRoleColor} style={{ marginInlineEnd: 0 }}>
              {message.sender_role_display}
            </Tag>
            {!message.is_read && (
              <Tooltip title={t("tickets.unread")}>
                <Tag color="orange">●</Tag>
              </Tooltip>
            )}
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(message.created).format("DD/MM/YYYY HH:mm")}
          </Text>
        </div>
        <div
          className="ticket-message-body"
          dangerouslySetInnerHTML={{ __html: message.body_html }}
          style={{
            fontSize: 14,
            lineHeight: 1.55,
            // Mismo motivo que en resolution_notes / description:
            // URLs / hashes largos sin espacios rompían la burbuja.
            wordBreak: "break-word",
            overflowWrap: "anywhere",
          }}
        />
        {message.attachments.length > 0 && (
          <div
            style={{
              marginTop: 12,
              borderTop: `1px solid ${borderColor}`,
              paddingTop: 8,
            }}
          >
            <Text
              type="secondary"
              style={{ fontSize: 12, display: "block", marginBottom: 6 }}
            >
              {t("tickets.attachments")}
            </Text>
            <AttachmentList attachments={message.attachments} />
          </div>
        )}
      </div>
    </div>
  );
};

export const TicketThread: React.FC<TicketThreadProps> = ({
  ticketId,
  onChanged,
  emptyDescription,
}) => {
  const { t } = useTranslation();
  const { message: notify } = AntdApp.useApp();
  const { data: identity } = useGetIdentity<UserInfo>();

  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [replyHtml, setReplyHtml] = useState("");
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [sending, setSending] = useState(false);

  // OJO con las dependencias: si añadimos `onChanged` al deps array
  // y el padre pasa una función inline nueva en cada render, `reload`
  // cambia identidad → useEffect se re-ejecuta → refetch → onChanged
  // → padre se re-renderiza → onChanged inline nueva → loop infinito
  // de fetches. Este exact bug tumbó `/api/v1/tickets/*` a 503 en
  // prod recién desplegada. Guardamos `onChanged` en un ref para
  // desacoplarlo del ciclo de reload sin renunciar a llamarlo.
  const onChangedRef = useRef(onChanged);
  useEffect(() => {
    onChangedRef.current = onChanged;
  }, [onChanged]);

  const reload = useCallback(
    async ({ notify: shouldNotify = false }: { notify?: boolean } = {}) => {
      setLoading(true);
      try {
        const data = await ticketService.getTicket(ticketId);
        setTicket(data);
        // El backend marca los mensajes como leídos en el GET del
        // detalle → notificamos al sider para que el badge se actualice
        // sin esperar al poll. Esto NO es lo mismo que `onChanged`
        // (que sí queremos ejecutar solo cuando hay una acción del
        // usuario que altera el hilo).
        notifyTicketsUnreadChanged();
        if (shouldNotify) {
          onChangedRef.current?.();
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        setLoading(false);
      }
    },
    [ticketId],
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  const onSendReply = async () => {
    if (!ticket) return;
    if (!replyHtml || replyHtml === "<p><br></p>") return;
    setSending(true);
    try {
      await ticketService.addMessage(ticket.id, {
        body_html: replyHtml,
        attachments: files
          .map((f) => f.originFileObj as File | undefined)
          .filter((f): f is File => !!f),
      });
      notify.success(t("tickets.reply_success"));
      setReplyHtml("");
      setFiles([]);
      // Aquí sí: hemos cambiado el estado del hilo, el padre debe
      // refrescar (contadores, listado).
      await reload({ notify: true });
    } catch {
      notify.error(t("tickets.reply_error"));
    } finally {
      setSending(false);
    }
  };

  if (loading && !ticket) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <Spin />
      </div>
    );
  }

  if (!ticket) {
    return (
      <Empty
        description={t(
          "tickets.thread_not_found",
          "No se pudo cargar el hilo.",
        )}
      />
    );
  }

  const isOpen = ticket.status === 1;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      {ticket.messages.length === 0 ? (
        <Empty
          description={
            emptyDescription ??
            t("tickets.no_messages_yet", "No hay comentarios todavía.")
          }
        />
      ) : (
        ticket.messages.map((m) => (
          <MessageBubble
            key={m.id}
            message={m}
            isMine={m.sender === identity?.app_user?.id}
          />
        ))
      )}

      {isOpen && (
        <div
          style={{
            marginTop: 8,
            padding: 12,
            border: "1px solid #f0f0f0",
            borderRadius: 8,
          }}
        >
          <RichTextEditor
            value={replyHtml}
            onChange={setReplyHtml}
            placeholder={t("tickets.reply_placeholder", "Escribe tu respuesta…")}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 8,
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <Upload
              fileList={files}
              onChange={({ fileList }) => setFiles(fileList)}
              beforeUpload={() => false}
              multiple
            >
              <Button icon={<PaperClipOutlined />}>
                {t("tickets.attach", "Adjuntar")}
              </Button>
            </Upload>
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={sending}
              onClick={onSendReply}
              disabled={!replyHtml || replyHtml === "<p><br></p>"}
            >
              {t("tickets.send", "Enviar")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketThread;
